import { IItem } from '../../timeline/store/timeline.types'
import { IEKGEvent } from '../store/ekgImport.types'
import { IDrainState } from '../../procedures/drain/store/drain.types'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { IOptions } from '../../../common/common.types'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { generateClientCorrelationId } from '../../nemsisData/utils/customElements.util'
import { getUpdatedProcedureStateWithNemsisCode, handleNemsisCodeError } from '../../../utilities/nemsisCode.util'

interface CreateDrainProcedureProps {
  procedureEvent: IEKGEvent
  drainOptions: IOptions
  ptaCutoff: Date | null
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface DrainProcedureObject {
  timelineItem: IItem
  drainState: IDrainState
}

const createDrainProcedure = (props: CreateDrainProcedureProps): DrainProcedureObject | null => {
  const { procedureEvent, drainOptions, ptaCutoff, nemsisCodeOptions } = props

  if (!procedureEvent.procedureAction) return null

  const clientEventId = (procedureEvent.eventVitals.clientEventId as number).toString()
  const vsAction = procedureEvent.eventVitals.vsAction
  const isEkgImport = procedureEvent.eventVitals.isEkgImport

  const drainOption = drainOptions.find((option) => (option.id ?? '').toString() === procedureEvent.procedureAction)
  const itemComments = procedureEvent.procedureComments || ''
  const itemSubHeader = `By , Action: ${
    drainOption?.name || ''
  }, Attempts: 1, Success: true, Comments: ${itemComments}.`

  const eventDate = new Date(procedureEvent.dtDevice)
  const isPTA = !!ptaCutoff && eventDate < ptaCutoff

  const clientCorrelationId = generateClientCorrelationId(
    nemsisCodeOptions,
    TimelineConstants.DRAIN_PROCEDURE,
    drainOption?.name || '',
    {
      filter1: null,
      filter2: null,
      filter3: null,
    },
    clientEventId
  )

  const timelineItem: IItem = {
    key: clientEventId,
    clientCorrelationId,
    itemType: 'Procedures',
    event: 'Drain',
    grid: true,
    labelHeaderFlag: isPTA ? 'PTA' : '',
    labelHeader: procedureEvent.eventVitals.time,
    subHeader: procedureEvent.eventVitals.date,
    eventSubHeader: itemSubHeader,
    rowActionType: TimelineConstants.SET_DRAIN_PROCEDURE_ITEM,
    defaultHref: TabConstants.DRAIN_PROCEDURE,
    date: new Date(procedureEvent.dtDevice),
    clientEventId: clientEventId,
    ...(vsAction && { vsAction: vsAction }),
    ...(isEkgImport && { isEkgImport: isEkgImport }),
  }

  let drainState: IDrainState = {
    key: clientEventId,
    date: procedureEvent.eventVitals.date,
    time: procedureEvent.eventVitals.time,
    assessedBy: null,
    rolePerforming: null,
    rolePerformingPNNV: null,
    procedureComplications: [],
    response: null,
    responsePNNV: null,
    authorization: null,
    comments: itemComments,
    successfulAttempt: true,
    pta: isPTA,
    attempts: '1',
    action: drainOption || null,
    procedure: null,
    size: '',
    field2: null,
    sizeUnit: null,
    drainSuction: null,
    chestSuction: null,
    foleyColor: null,
    csfColor: null,
    capped: null,
    side: null,
    location: null,
    intercostalSpace: null,
    depth: '',
    updateInputRows: false,
    type: 'Drain',
    addToTimeline: true,
    chestSuctionHiddenFlag: false,
    csfColorHiddenFlag: false,
    depthHiddenFlag: false,
    drainSuctionHiddenFlag: false,
    foleyColorHiddenFlag: false,
    procedureHiddenFlag: false,
    sideHiddenFlag: false,
    sizeHiddenFlag: false,
    sizeUnitHiddenFlag: false,
    isEkgImport: 1,
    drainDefaults: null,
    nemsisCode: null,
    procedurePerformedByAnotherUnit: null,
    procedurePerformedByAnotherUnitPNNV: null,
  }

  try {
    drainState = getUpdatedProcedureStateWithNemsisCode({
      procedureState: drainState,
      actionField: (s) => s.action?.name,
      filter1Field: null,
      filter2Field: null,
      filter3Field: null,
      nemsisCodeOptions,
    })
  } catch (error: unknown) {
    drainState = handleNemsisCodeError(error, drainState)
  }

  const drainProcedureObject: DrainProcedureObject = {
    drainState,
    timelineItem,
  }

  return drainProcedureObject
}

export default createDrainProcedure
